import { useState } from 'react'
import { useNavigate, useLocation, Navigate } from 'react-router-dom'
import { Check, Upload } from 'lucide-react'
import { useCaseStore, useProfileStore } from '../../stores'
import { useI18n } from '../../i18n'
import { CompactHeader } from '../../components/OrellaChatHeader'
import PrimaryCareDoctor from '../../components/PrimaryCareDoctor'
import { referralRequestsAPI } from '../../services/api'
import { caseBack } from '../../utils/caseBack'
import { formatUsPhone } from '../../utils/caseReport'
import { clinicianName, withCaseClinician } from '../../utils/clinicianName.js'
import { OrellaSpinner } from '../../components/OrellaLoader' // s123: the app's one spinner

/**
 * ReferralRequestScreen — s106, canvas "Orella Referrals Screens" A3 (PCP on Orella: their inbox),
 * A3b (PCP not on Orella: Orella faxes the office, API D515) and A4 (sent). Copy approved 2026-10-16
 * (claude/ORELLA_REFERRAL_APP_COPY_v1.md). Route /case/referral-request; state {doctorName}.
 * The specialist already holds the case (the share step sent it first, decision 1); booking with them
 * stays locked until the referral is on file (server: _referral_lock_refusal).
 * The patient's PCP is insurance.pcp (D497); with none on file the PCP picker is shown first.
 */
const NAVY = '#15354F'
const MUTED = '#4E6577'
const card = { background: '#fff', border: '1px solid #DCE6EE', borderRadius: 16, padding: 14 }
const faxDigits = (v) => { const d = String(v || '').replace(/\D/g, '').replace(/^1(?=\d{10}$)/, ''); return d.length === 10 ? d : '' }

export default function ReferralRequestScreen() {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useI18n()
  const { currentCase } = useCaseStore()
  const profile = useProfileStore((s) => s.profile)
  const savedPcp = profile?.insurance?.pcp || null
  const [pcp, setPcp] = useState(savedPcp)
  const [fax, setFax] = useState(savedPcp?.fax ? formatUsPhone(savedPcp.fax) || savedPcp.fax : '')
  const [includeReport, setIncludeReport] = useState(true)
  const [consent, setConsent] = useState(false)
  const [phase, setPhase] = useState('form') // form | sending | sent
  const [error, setError] = useState('')
  const [failedRequest, setFailedRequest] = useState(null)

  if (!currentCase) return <Navigate to="/home" replace />
  const doc = currentCase.doctor || {}
  const doctorName = (location.state && location.state.doctorName)
    || (doc.last_name || doc.lastName ? clinicianName(withCaseClinician(doc, currentCase)) : '')
  const onOrella = !!(pcp && pcp.doctorId)
  const faxOk = onOrella || !!faxDigits(fax)
  const pcpName = pcp?.name || ''
  const reason = [currentCase.focusAreaName || currentCase.focus_area, currentCase.chief_complaint].filter(Boolean).join(' · ')

  const send = async () => {
    if (!pcp || !consent || !faxOk || phase === 'sending') return
    setPhase('sending'); setError('')
    try {
      if (failedRequest) {
        await referralRequestsAPI.resendFax(failedRequest.id, { consent: true, faxNumber: faxDigits(fax) })
        setPhase('sent'); return
      }
      const res = await referralRequestsAPI.create(currentCase.id, {
        pcp: { doctorId: pcp.doctorId || undefined, npi: pcp.npi || undefined, name: pcp.name,
               fax: onOrella ? undefined : faxDigits(fax), phone: pcp.phone || undefined, address: pcp.address || undefined },
        includeCaseReport: includeReport, consent: true,
      })
      if (res?.faxError) {
        setFailedRequest(res.request || null)
        setError(t('referral.sendFailed', "We couldn't send your request. Please try again."))
        setPhase('form'); return
      }
      setPhase('sent')
    } catch (e) {
      if (e?.code === 'request_open') { navigate('/case/home', { replace: true }); return }
      setError(e?.code === 'bad_fax' || e?.code === 'fax_invalid'
        ? t('referral.faxInvalid', 'Enter a 10-digit US fax number.')
        : t('referral.sendFailed', "We couldn't send your request. Please try again."))
      setPhase('form')
    }
  }

  if (phase === 'sent') {
    return (
      <div className="min-h-full bg-gray-50 flex flex-col" data-testid="referral-sent">
        <div className="flex-1 px-5 pt-24 pb-6 flex flex-col gap-3.5">
          <span className="self-center w-[72px] h-[72px] rounded-full grid place-items-center text-white" style={{ background: '#15803D' }}><Check size={36} /></span>
          <h1 className="m-0 text-[22px] font-extrabold text-center" style={{ color: NAVY }}>{t('referral.sentTitle', 'Referral request sent')}</h1>
          <p className="m-0 text-[14px] text-center leading-normal" style={{ color: MUTED }}>
            {t('referral.sentBody', { defaultValue: "We asked {{pcp}}'s office for your referral to {{doctor}}. {{doctor}} has your case.", pcp: pcpName, doctor: doctorName })}
          </p>
          <div style={card} className="flex flex-col gap-2">
            {[t('referral.sentStep1', 'The office reviews the request, usually within 1 to 3 business days.'),
              t('referral.sentStep2', { defaultValue: "When the referral is on file, we'll let you know and booking with {{doctor}} opens.", doctor: doctorName }),
              t('referral.sentStep3', 'If you get the referral yourself, you can upload it from your case.')].map((line, i) => (
              <div key={i} className="flex gap-2.5 text-[13.5px] leading-snug" style={{ color: NAVY }}>
                <span className="flex-none w-[22px] h-[22px] rounded-full grid place-items-center text-[12px] font-extrabold" style={{ background: '#E9F8FF' }}>{i + 1}</span>
                <span>{line}</span>
              </div>
            ))}
          </div>
          <button type="button" onClick={() => navigate('/case/home', { replace: true })} data-cta="referral-back-to-case"
                  className="mt-auto w-full py-3.5 rounded-[14px] text-[15px] font-extrabold text-white" style={{ background: NAVY }}>
            {t('referral.backToCase', 'Back to my case')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-full bg-gray-50 flex flex-col" data-testid="referral-request">
      <CompactHeader stepLabel={t('referral.askTitle', 'Ask for a referral')} onBack={() => caseBack(navigate)} onNotification={() => navigate('/notifications')} />
      <div className="flex-1 px-4 pt-4 pb-6 flex flex-col gap-3">
        <div style={card} className="flex flex-col gap-1">
          <span className="text-[11px] font-bold tracking-[.08em] uppercase" style={{ color: MUTED }}>{t('referral.requestFrom', 'Request a referral from')}</span>
          {pcp ? (
            <>
              <p className="m-0 text-[16px] font-extrabold" style={{ color: NAVY }}>{pcp.name}{pcp.credential ? `, ${pcp.credential}` : ''}</p>
              {(pcp.specialty || pcp.address) && <p className="m-0 text-[13px] leading-snug" style={{ color: MUTED }}>{[pcp.specialty, pcp.address].filter(Boolean).join(' · ')}</p>}
              <span className="self-start mt-1 text-[12px] font-bold rounded-full px-2.5 py-1"
                    style={onOrella ? { background: '#E3F6E8', color: '#166534' } : { background: '#FEF3C7', color: '#92400E' }}>
                {onOrella ? t('referral.onOrellaInbox', "On Orella · goes to their office's inbox") : t('referral.notOnOrellaFax', "Not on Orella · we'll fax the office")}
              </span>
            </>
          ) : (
            <PrimaryCareDoctor value={null} cardPcp={profile?.insurance?.pcpFromCard || null} coverageType={profile?.insurance?.coverageType}
              zip={profile?.zipCode || profile?.address?.zipCode || profile?.address?.zip || ''}
              onChange={(p) => { setPcp(p); setFax(p?.fax ? formatUsPhone(p.fax) || p.fax : '') }} t={t} />
          )}
        </div>
        {pcp && !onOrella && (
          <>
            <label className="flex flex-col gap-1.5 text-[13px] font-bold" style={{ color: NAVY }}>
              {t('referral.faxLabel', 'Office fax number')}
              <input type="tel" inputMode="tel" value={fax} onChange={(e) => setFax(e.target.value)} data-testid="referral-fax"
                     className="rounded-xl px-3 py-3 text-[15px] font-semibold bg-white" style={{ border: '1px solid #B7C7D6', color: NAVY }} />
            </label>
            {!savedPcp?.fax && !faxDigits(fax) && (
              <p className="m-0 text-[13px] leading-snug" style={{ color: MUTED }}>{t('referral.faxMissing', "We didn't find a fax for this office. Add it from their website or by calling them.")}</p>
            )}
          </>
        )}
        <div style={card} className="flex flex-col gap-1">
          <span className="text-[11px] font-bold tracking-[.08em] uppercase" style={{ color: MUTED }}>{t('referral.referralTo', 'Referral to')}</span>
          <p className="m-0 text-[16px] font-extrabold" style={{ color: NAVY }}>{doctorName}</p>
          {reason && <p className="m-0 text-[13px]" style={{ color: MUTED }}>{reason}</p>}
        </div>
        <label className="flex gap-2.5 items-start text-[13.5px] leading-snug" style={{ color: NAVY }}>
          <input type="checkbox" checked={includeReport} onChange={(e) => setIncludeReport(e.target.checked)} className="w-5 h-5 mt-0.5 flex-none" style={{ accentColor: NAVY }} />
          <span>{t('referral.includeReport', 'Include my Case Report so the office knows why I need the referral')}</span>
        </label>
        <label className="flex gap-2.5 items-start text-[13.5px] leading-snug" style={{ color: NAVY }}>
          <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="referral-consent" className="w-5 h-5 mt-0.5 flex-none" style={{ accentColor: NAVY }} />
          <span>{onOrella
            ? t('referral.consentInbox', "I ask Orella to send this request to my primary care doctor's office for me")
            : t('referral.consentFax', "I ask Orella to fax this request to my primary care doctor's office for me")}</span>
        </label>
        <p className="m-0 text-[13px] leading-snug" style={{ color: MUTED }}>
          {t('referral.bookNote', { defaultValue: '{{doctor}} receives your case now. Booking with {{doctor}} opens once the referral is on file.', doctor: doctorName })}
        </p>
        {error && <p className="m-0 text-[13px] font-semibold" style={{ color: '#B91C1C' }} role="alert">{error}</p>}
        <button type="button" onClick={send} disabled={!pcp || !consent || !faxOk || phase === 'sending'} data-cta="referral-send-request"
                className="mt-auto w-full py-3.5 rounded-[14px] text-[15px] font-extrabold text-white flex items-center justify-center gap-2 disabled:opacity-50"
                style={{ background: '#15803D' }}>
          {phase === 'sending' && <OrellaSpinner size={16} tone="inherit" />}
          {failedRequest ? t('referral.tryAgain', 'Try again')
            : onOrella ? t('referral.sendInbox', 'Send referral request') : t('referral.sendFax', 'Fax referral request')}
        </button>
        <button type="button" onClick={() => navigate('/case/referral-upload', { state: { doctorName } })} data-cta="referral-skip-upload"
                className="text-[14px] font-bold py-1.5 flex items-center justify-center gap-1.5" style={{ color: '#1E6FB8' }}>
          <Upload size={14} /> {t('referral.skipUpload', "Skip · I'll get the referral and upload it")}
        </button>
      </div>
    </div>
  )
}
